import { Controller, type Control, type FieldErrors } from 'react-hook-form';
import { Checkbox } from '@shared/components';
import type { AccessPromptFormInput, AccessPromptFormValues } from '../../schemas/accessPrompt.schema';
import type { CallerConfirmationDetails } from '../../types/agreementAccess.types';
import { CONFIRMATION_DETAIL_ROWS } from '../../utils/callerConfirmation';
import { SECTION_HEADING_CLASSES } from './sectionStyles';

interface Props {
    idPrefix: string;
    details: CallerConfirmationDetails;
    control: Control<AccessPromptFormInput, unknown, AccessPromptFormValues>;
    errors: FieldErrors<AccessPromptFormInput>;
    disabled: boolean;
}

const NOT_HELD = 'Not held';
const HEADING = "Confirm the customer's details";
const HINT =
    'Ask the person contacting you for each detail and tick its box once they have given it correctly. These are the details recorded when the plan was sold.';

// The shared Checkbox owns its label element, so the box stretches it from here to make the whole box the target.
const BOX_CLASSES = [
    'rounded-md border border-mp-border',
    'has-[:checked]:border-mp-primary has-[:checked]:bg-mp-primary-content',
    '[&>label]:flex [&>label]:w-full [&>label]:p-3',
].join(' ');

export function CallerDetailsFields({ idPrefix, details, control, errors, disabled }: Props) {
    const headingId = `${idPrefix}-details-heading`;

    return (
        <div role="group" aria-labelledby={headingId}>
            <h3 id={headingId} className={SECTION_HEADING_CLASSES}>
                {HEADING}
            </h3>
            <p className="mb-3 mt-1 text-sm text-mp-muted">{HINT}</p>
            <ul className="space-y-2">
                {CONFIRMATION_DETAIL_ROWS.map(({ field, detail, label }) => {
                    const value = details[detail];
                    const error = errors[field]?.message;

                    return (
                        <li key={field}>
                            <div className={BOX_CLASSES}>
                                <Controller
                                    name={field}
                                    control={control}
                                    render={({ field: tick }) => (
                                        <Checkbox
                                            id={`${idPrefix}-${field}`}
                                            label={<DetailText label={label} value={value} />}
                                            ariaLabel={`${label}: ${value ?? NOT_HELD}`}
                                            checked={tick.value}
                                            onChange={tick.onChange}
                                            disabled={value === null || disabled}
                                        />
                                    )}
                                />
                            </div>
                            {error && (
                                <p role="alert" className="mt-1 text-xs text-mp-error">
                                    {error}
                                </p>
                            )}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

function DetailText({ label, value }: { label: string; value: string | null }) {
    return (
        <>
            <span className="block text-xs text-mp-muted">{label}</span>
            <span className={value === null ? 'block italic text-mp-muted' : 'block font-medium'}>
                {value ?? NOT_HELD}
            </span>
        </>
    );
}
